import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { canViewPrices } from '../../utils/pricing'; // إخفاء عمود السعر عن الأدوار غير 1/3/7
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const FarrierMaterialsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = String(i18n.language || '').startsWith('ar');
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [stockQty, setStockQty] = useState('');
  const [disposalQty, setDisposalQty] = useState('');
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [disposalDialogOpen, setDisposalDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/farrier_materials`);
      setMaterials(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('farrierMaterials.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const filteredMaterials = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return materials;
    return materials.filter((material) => (
      String(material.medication_name || '').toLowerCase().includes(term)
      || String(material.notes || '').toLowerCase().includes(term)
    ));
  }, [materials, search]);

  const {
    page,
    rowsPerPage,
    paginatedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredMaterials, [search]);

  const openStockDialog = useCallback((materialId) => {
    setSelectedMaterialId(materialId);
    setStockQty('');
    setStockDialogOpen(true);
  }, []);

  const openDisposalDialog = useCallback((materialId) => {
    setSelectedMaterialId(materialId);
    setDisposalQty('');
    setDisposalDialogOpen(true);
  }, []);

  const submitQuantity = useCallback(async (mode) => {
    const quantity = Number(mode === 'stock' ? stockQty : disposalQty);
    const userId = Number(localStorage.getItem('userId'));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError(t('farrierMaterials.invalidQuantity'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.post(`${apiUrl}/farrier_materials/${mode === 'stock' ? 'add-stock' : 'dispose-stock'}`, {
        medication_id: selectedMaterialId,
        quantity,
        user_id: userId || null,
      });
      setStockDialogOpen(false);
      setDisposalDialogOpen(false);
      setMessage(t(mode === 'stock' ? 'farrierMaterials.stockAddedSuccess' : 'farrierMaterials.stockDisposedSuccess'));
      await fetchMaterials();
    } catch (err) {
      setError(err?.response?.data?.error || t(mode === 'stock' ? 'farrierMaterials.stockAddError' : 'farrierMaterials.stockDisposeError'));
    } finally {
      setSaving(false);
    }
  }, [disposalQty, fetchMaterials, selectedMaterialId, stockQty, t]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/farrier_materials/${deleteId}`);
      setDeleteId(null);
      setMessage(t('farrierMaterials.deleteSuccess'));
      await fetchMaterials();
    } catch (err) {
      setError(err?.response?.data?.error || t('farrierMaterials.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [deleteId, fetchMaterials, t]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('farrierMaterials.title')}
        subtitle={t('farrierMaterials.pageHint')}
        count={materials.length}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/farrier-material-add')} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('farrierMaterials.addNew')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('farrierMaterials.search')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('farrierMaterials.name')}</TableCell>
                <TableCell align="center">{t('farrierMaterials.stock')}</TableCell>
                {canViewPrices() && <TableCell align="center">{t('farrierMaterials.price')}</TableCell>}
                <TableCell align="center" sx={{ minWidth: 220 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((material, index) => (
                <TableRow key={material.medication_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 190 }}>{material.medication_name || '-'}</TableCell>
                  <TableCell align="center">{material.stock_quantity ?? 0}</TableCell>
                  {canViewPrices() && <TableCell align="center">{Number(material.price || 0).toLocaleString('en-US')} SAR</TableCell>}
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit')}>
                        <IconButton onClick={() => navigate(`/farrier-material-edit/${material.medication_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('farrierMaterials.addToStock')}>
                        <IconButton onClick={() => openStockDialog(material.medication_id)} sx={smartActionButtonSx(COLORS.green, COLORS.greenSoft)}>
                          <InventoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('farrierMaterials.dispose')}>
                        <IconButton onClick={() => openDisposalDialog(material.medication_id)} sx={smartActionButtonSx(COLORS.warning, COLORS.warningSoft)}>
                          <DeleteSweepIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => setDeleteId(material.medication_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredMaterials.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('farrierMaterials.noMaterials')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination count={filteredMaterials.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>

      <Dialog open={stockDialogOpen} onClose={() => setStockDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('farrierMaterials.enterStockAmount')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus type="number" label={t('farrierMaterials.quantity')} fullWidth value={stockQty} onChange={(event) => setStockQty(event.target.value)} sx={{ mt: 1 }} inputProps={{ min: 0, step: '1' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStockDialogOpen(false)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={() => submitQuantity('stock')} disabled={saving} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={disposalDialogOpen} onClose={() => setDisposalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('farrierMaterials.disposeStock')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus type="number" label={t('farrierMaterials.enterDisposalAmount')} fullWidth value={disposalQty} onChange={(event) => setDisposalQty(event.target.value)} sx={{ mt: 1 }} inputProps={{ min: 0, step: '1' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisposalDialogOpen(false)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={() => submitQuantity('dispose')} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('farrierMaterials.confirmDelete')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarrierMaterialsList;
